import FORM_FIELDS from '@/components/FormFields';
import Map from '@/components/Map';
import Div from '@/shared/Div';
import Form from '@/shared/Form';
import { MapType } from '@/types';
import { REGEX_MAP } from '@/utils/formValidation';
import React, { useCallback, useReducer } from 'react';
import { Controller, useForm } from 'react-hook-form';

export interface FormField {
  fieldType: 'Text' | 'Select' | 'Textarea' | 'Heading' | 'Checkbox' | 'Button' | 'Number';
  name: string;
  id: string;
  placeholder?: string;
  label?: string;
  rules?: any;
  className?: string;
  info?: string;
  value?: string;
  options?: any[];
  wrapperClassName?: string;
  theme?: string; //For Button
  type?: string; // For Button
}
interface FormRendererProps {
  fields: FormField[];
  className?: string;
  submitHandler: (data?: any) => void;
}

const NON_FORM_FIELDS: MapType = {
  Heading: true,
  Button: true,
};

type FormRendererState = {
  fields: [] | FormField[];
};

type FormRendererAction = {
  type: string;
  payload?: any;
};

const formRendererReducer = (state: FormRendererState, action: FormRendererAction) => {
  const { type, payload = {} } = action;
  const { id, value } = payload;
  switch (type) {
    case 'UPDATE_FIELD_BY_ID':
      const fields = state.fields.map((field: FormField) => {
        if (field.id === id) {
          return { ...field, ...(value ?? {}) };
        }
        return field;
      });
      return { ...state, fields };
    default:
      return state;
  }
};

const getDefaultValues = (fields: FormField[]) => {
  let defaultValues: any = {};
  for (const field of fields) {
    if (field.value) {
      defaultValues[field.id] = field.value;
    }
  }
  return defaultValues;
};

const regexifyRules = (rules: any) => {
  let validationRules = Object.assign({}, rules);
  const patternValue = validationRules?.pattern?.value;
  if (patternValue && patternValue in REGEX_MAP) {
    validationRules = Object.assign(
      {},
      {
        ...validationRules,
        pattern: {
          value: REGEX_MAP[patternValue],
          message: validationRules?.pattern?.message,
        },
      },
    );
  }
  return validationRules;
};

const useFormRenderer = ({ fields, className, submitHandler }: FormRendererProps) => {
  const [state, dispatch] = useReducer(formRendererReducer, { fields });
  const updateFieldById = (id: string, value: any) => {
    dispatch({ type: 'UPDATE_FIELD_BY_ID', payload: { id, value } });
  };
  const form = useForm({
    defaultValues: getDefaultValues(fields),
    mode: 'all',
  });
  const { control, handleSubmit } = form;

  const fieldRenderer = useCallback(
    (formField: FormField) => {
      const { fieldType, rules, wrapperClassName, ...restFormFieldProps } = formField;
      const FormFieldComponent = FORM_FIELDS[fieldType];
      const Wrapper = wrapperClassName ? Div : React.Fragment;
      const wrapperProps: any = {};
      if (wrapperClassName) wrapperProps.className = wrapperClassName;

      if (FormFieldComponent) {
        let tsx = null;

        if (NON_FORM_FIELDS[fieldType]) {
          tsx = <FormFieldComponent key={restFormFieldProps.id} {...restFormFieldProps} />;
        } else {
          tsx = (
            <Controller
              key={restFormFieldProps.id}
              rules={regexifyRules(rules)}
              {...restFormFieldProps}
              control={control}
              render={({ field, formState }) => {
                const compProps: any = {};
                if (fieldType === 'Select') {
                  compProps['selectedValue'] = field.value;
                }
                return (
                  <Wrapper {...wrapperProps}>
                    <FormFieldComponent
                      {...restFormFieldProps}
                      {...field}
                      {...compProps}
                      formState={formState}
                    />
                  </Wrapper>
                );
              }}
            />
          );
        }
        return (
          <Wrapper key={restFormFieldProps?.id} {...wrapperProps}>
            {tsx}
          </Wrapper>
        );
      }
      return null;
    },
    [control],
  );

  const tsx = (
    <Form className={className} onSubmit={handleSubmit(submitHandler)}>
      <Map list={state.fields} render={fieldRenderer} />
    </Form>
  );

  return {
    form,
    tsx,
    updateFieldById,
    fields: state.fields,
    fieldRenderer,
  };
};

export default useFormRenderer;
